import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useCallback, useMemo, useRef, useState } from "react";

interface UseSubmitKorrekturConfirmDialogOptions {
	isSubmitting: boolean;
	onConfirmSubmit: () => void | Promise<void>;
}

/**
 * Local hook to encapsulate the "Korrektur abgeben?" confirmation dialog.
 *
 * Includes a pointer/pen fallback: on iPad/Safari an Apple Pencil interaction can
 * sometimes suppress the synthetic `click` event; handling `pointerup` for `pen`
 * ensures the confirm action still triggers.
 */
export function useSubmitKorrekturConfirmDialog({
	isSubmitting,
	onConfirmSubmit,
}: UseSubmitKorrekturConfirmDialogOptions) {
	const [isOpen, setIsOpen] = useState(false);

	// If we trigger submit via pointerup (pen), ignore the subsequent click to avoid double-submit.
	const ignoreNextClickRef = useRef(false);

	const close = useCallback(() => setIsOpen(false), []);
	const open = useCallback(() => setIsOpen(true), []);

	const confirm = useCallback(() => {
		setIsOpen(false);
		void onConfirmSubmit();
	}, [onConfirmSubmit]);

	const handleConfirmClick = useCallback(() => {
		if (ignoreNextClickRef.current) {
			ignoreNextClickRef.current = false;
			return;
		}
		confirm();
	}, [confirm]);

	const handleConfirmPointerUp = useCallback(
		(e: React.PointerEvent) => {
			// Apple Pencil typically reports pointerType="pen".
			if (e.pointerType !== "pen") return;
			ignoreNextClickRef.current = true;
			// Prevent an additional synthetic click in some browsers.
			e.preventDefault();
			confirm();
		},
		[confirm],
	);

	const dialog = useMemo(
		() => (
			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogContent className="sm:max-w-[500px] bg-background-secondary">
					<DialogHeader>
						<DialogTitle>Korrektur abgeben?</DialogTitle>
						<DialogDescription>
							Bist du sicher, dass du die Korrektur abgeben möchtest? Danach
							kannst du sie nicht mehr bearbeiten.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={close}
							disabled={isSubmitting}
						>
							Abbrechen
						</Button>
						<Button
							type="button"
							variant="default"
							onClick={handleConfirmClick}
							onPointerUp={handleConfirmPointerUp}
							disabled={isSubmitting}
						>
							{isSubmitting ? "Wird abgegeben..." : "Abgeben"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		),
		[close, handleConfirmClick, handleConfirmPointerUp, isOpen, isSubmitting],
	);

	return {
		isOpen,
		open,
		close,
		setIsOpen,
		dialog,
	} as const;
}
